import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, take, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { MatDialog } from '@angular/material/dialog';

import { environment } from 'src/environments/environment';
import { TelemetryService } from 'src/app/services/telemetry.service';
import {
  BillingType,
  BillingFrequency,
  BillingStrategy,
  CheckoutEligibilityAllowedResponse,
  CheckoutEligibilityError,
} from '@pncl/common-models';
import { StartTrialConfirmationModalComponent } from '../dialogs/start-trial-confirmation-modal/start-trial-confirmation-modal.component';
import {
  CheckoutBlockedModalComponent,
  CheckoutBlockedModalData,
} from '../dialogs/checkout-blocked-modal/checkout-blocked-modal.component';
import {
  PostPayment,
  CheckoutInfo,
  HubspotContactProperties,
  PortalInfo,
  SubscriptionInfo,
  ChargebeeSubscriptionStatus,
  CustomerPlan,
  CheckoutSuccessResponse,
  PaymentPlan,
  ChargebeeSubscriptionData,
  SubscriptionStatus,
  Subscription,
} from '../models/payment';
import { isCheckoutEligibilityError } from '../models/checkout-eligibility';
import { IconMessageToasterElement } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { ERRORS, SUCCESSES } from '../common/utils/notification-constants';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { modifiedSetInterval, modifiedSetTimeout } from '../utilities/ZoneUtils';
import { buildPaywallConfigFromPaymentPlan } from '../utilities/payment-plan-paywall.utils';
import { Institution, UserData } from '../models/user';
import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';

import { InstitutionDetailsModalComponent } from '../dialogs/institution-details-modal/institution-details-modal.component';
import { ActionsQueryParamKeys } from '../common/utils/actions-query-params-util';
import { HomeNavigationService } from './home-navigation.service';
import { URLService } from './dynamic-url.service';
import { SettingsService } from './settings.service';
import {
  NotificationDataBuilder,
  NotificationToasterService,
  NotificationType,
} from './notification-toaster.service';
import { FullScreenModalService } from './full-screen-modal.service';
import { UserService } from './user.service';
import { PlanDetailsService } from './plan-details.service';
import { FLAGS, FlagsService } from './flags.service';
import { ModalManagerService } from './modal-manager.service';

export enum PopUpPageType {
  CreateSubscribtion = 'CreateSubscribtion',
  SwitchSubscription = 'SwitchSubscription',
  CancelSubscription = 'CancelSubscription',
}

interface PayWallNotificationConfigurtion {
  icon: string;
  notificationTopElement: string;
  notificationType: NotificationType;
  toasterPopupStyle: ToasterPopupStyle;
  notificationMiddleElement: string;
  telemetryEventData?: string;
}

const notificationConfigurationMap: Record<
  PopUpPageType | 'Failure',
  PayWallNotificationConfigurtion
> = {
  [PopUpPageType.CreateSubscribtion]: {
    icon: 'check',
    notificationTopElement: "You're all set!",
    notificationType: NotificationType.SUCCESS,
    toasterPopupStyle: ToasterPopupStyle.SUCCESS,
    notificationMiddleElement:
      'Thank you for upgrading to Pro! This page will auto-refresh shortly as we get everything ready for you.',
    telemetryEventData: 'user_did_upgrade',
  },
  [PopUpPageType.SwitchSubscription]: {
    icon: 'check',
    notificationTopElement: "We've changed your plan",
    notificationType: NotificationType.SUCCESS,
    toasterPopupStyle: ToasterPopupStyle.SUCCESS,
    notificationMiddleElement: 'This page will refresh momentarily to reflect your new plan',
    telemetryEventData: 'user_did_switch',
  },
  [PopUpPageType.CancelSubscription]: {
    icon: 'arrow_downward',
    notificationTopElement: 'Plan downgraded',
    notificationType: NotificationType.INFO,
    toasterPopupStyle: ToasterPopupStyle.INFO,
    notificationMiddleElement:
      'We’re sorry to see you go! This page will refresh shortly with your new plan details',
    telemetryEventData: 'user_did_cancel',
  },
  ['Failure']: {
    icon: 'warning',
    notificationTopElement: 'Something went wrong',
    notificationType: NotificationType.ERROR,
    toasterPopupStyle: ToasterPopupStyle.ERROR,
    notificationMiddleElement: "Sorry, but we weren't able to adjust your plan. Please try again.",
  },
};

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  isLoggingEnabled = true;
  popoutWindow: Window | null = null;
  intervalId?: NodeJS.Timer;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private telemetry: TelemetryService,
    private fullScreenModalService: FullScreenModalService,
    private userService: UserService,
    private gtmService: GoogleTagManagerService,
    private planDetailsService: PlanDetailsService,
    private flagsService: FlagsService,
    private dialog: MatDialog,
    private modalManagerService: ModalManagerService,
    private homeNavigation: HomeNavigationService,
  ) {}

  openStartTrialModal(source: string): void {
    this.dialog.open(StartTrialConfirmationModalComponent, {
      width: 'auto',
      maxWidth: '100vw',
      disableClose: false,
      panelClass: 'start-trial-confirmation-dialog',
      data: { source },
    });
  }

  /**
   * If the user has pending upgrade config but no institution (webhook fallback state),
   * logs to FullStory and opens the Institution Details Modal so they can complete setup.
   * Called from AppComponent when the authenticated user stream emits.
   */
  handlePendingUpgradeFallbackIfNeeded(data: UserData): void {
    if (!data.user.pendingUpgradeConfiguration || data.user.institution) {
      return;
    }
    const config = data.user.pendingUpgradeConfiguration;
    this.telemetry.logBasicEvent('[Institution Upgrade Fallback Detected]', {
      source: 'institution_subscription_webhook_fallback',
      planId: config.subscriptionData?.planId,
      subscriptionId: config.subscriptionData?.subscriptionId,
    });
    this.handleInstitutionDetailsModal(config.subscriptionData, config.paymentPlan);
  }

  isSeatBasedInstitution(institution?: Institution | null): boolean {
    return institution?.paywallConfig?.billingStrategy === BillingStrategy.SEAT_BASED;
  }

  isV2BillingEnabled(institution?: Institution | null): boolean {
    return !!institution?.paywallConfig?.enableV2Billing;
  }

  /**
   * Opens a new browser window for payment processing.
   *
   * The window is initially opened with no URL (blank) during a direct user interaction event to avoid being blocked by popup blockers.
   * The URL of this window can be later set to load the desired content.
   * If a pop-out window is already open, it closes the existing window before opening a new one.
   *
   * SECURITY: Sets opener to null to prevent "Reverse Tabnabbing" where third-party payment pages
   * could redirect the parent window (e.g., window.opener.location = 'malicious-site.com').
   * This was causing payment success redirects to land in the main window instead of the popup.
   *
   * @param windowId - A string representing the window name.
   * @param width - The width of the window in pixels.
   * @param height - The height of the window in pixels.
   */
  openBufferWindow(windowId: string, width = 500, height = 550): void {
    this.closeWindowAndClearInterval();
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;
    try {
      this.popoutWindow = window.open(
        '',
        `${windowId}`,
        `resizable=yes, scrollbars=no, toolbar=no, menubar=no, width=${width}, height=${height}, top=${top}, left=${left}`,
      );

      if (this.popoutWindow) {
        // SECURITY FIX: Prevent "Reverse Tabnabbing"
        // Third-party payment pages (Chargebee, Stripe, etc.) can use window.opener.location
        // to redirect the parent window, causing payment success redirects to land in the main window
        // instead of staying in the popup. Setting opener to null prevents this behavior.
        // See: https://owasp.org/www-community/attacks/Reverse_Tabnabbing
        (this.popoutWindow as any).opener = null;
      }
    } catch {
      // Intentionally empty - window.open failures are handled by the null check below
    }
    if (!this.popoutWindow) {
      this.logMessages('Issue: the window was blocked and could not be opened.');
    }
  }

  handleError(error: any): void {
    if (isCheckoutEligibilityError(error?.error)) {
      this.closeWindowAndClearInterval();
      this.openCheckoutBlockedModal(error.error);
      return;
    }
    this.showToastError(error?.error?.err);
    this.logMessages(error.message);
  }

  openCheckoutBlockedModal(error: CheckoutEligibilityError): void {
    this.modalManagerService.showModal<CheckoutBlockedModalComponent, CheckoutBlockedModalData>(
      CheckoutBlockedModalComponent,
      {
        data: { error },
        width: '450px',
        disableClose: false,
        panelClass: 'checkout-blocked-modal-panel',
      },
    );
  }

  private async executeWithWindowRollback<T>(action: () => Promise<T>): Promise<T | void> {
    try {
      return await action();
    } catch (error) {
      this.closeWindowAndClearInterval();
      this.handleError(error);
    }
  }

  async handleCheckout(
    checkoutType: PopUpPageType,
    plan: PaymentPlan,
    isInstitutionUser: boolean,
  ): Promise<void> {
    await this.executeWithWindowRollback(async () => {
      const checkout: CheckoutInfo = await firstValueFrom(
        this.checkoutCallHandler(checkoutType, String(plan.planId)),
      );

      if (!checkout) {
        throw new Error('Checkout not available');
      }

      if (!this.popoutWindow) {
        this.showPopUpBlockedError();
        return;
      }

      this.popoutWindow.location.href = checkout.checkout_url;
      this.checkoutStateHandler(checkout, checkoutType, plan, isInstitutionUser);
    });
  }

  logMessages(message: string) {
    if (this.isLoggingEnabled) {
      console.log(`Paywall Popup Log: ${message}`);
    }
  }

  checkoutCallHandler(checkoutType: PopUpPageType, planID: string): Observable<CheckoutInfo> {
    if (checkoutType === PopUpPageType.CreateSubscribtion) {
      return this.createCheckout(planID);
    } else {
      return this.switchPlanCheckout(planID);
    }
  }

  checkoutStateHandler(
    checkout: CheckoutInfo,
    checkoutType: PopUpPageType,
    plan: PaymentPlan,
    isInstitutionUser: boolean,
  ): void {
    const checkPaymentStatus = () => {
      this.logMessages('popup listener running');
      if (this.popoutWindow?.closed) {
        this.logMessages('popup closed');
        this.closeWindowAndClearInterval();
        return;
      }
      try {
        if (this.popoutWindow?.location.search.includes('state=succeeded')) {
          this.logMessages('popup succeed state caught');
          this.closeWindowAndClearInterval();

          this.checkoutSucceed(checkout.hosted_page_id)
            .pipe(take(1))
            .subscribe(({ message, subscriptionData }) => {
              const isSuccess = message === 'Success';
              if (!isSuccess) {
                return this.showNotification(isSuccess, checkoutType, plan.planId);
              }
              if (subscriptionData) {
                // Add FullStory logging for institution upgrade success
                this.logInstitutionUpgradeToFullStory(plan, subscriptionData);

                // Track conversion with verified subscription data from Chargebee
                this.trackConversionWithSubscriptionData(plan, subscriptionData, checkoutType);

                if (isInstitutionUser) {
                  // Handle existing institution user billing update
                  this.handleInstitutionBillingUpdate(subscriptionData, plan);
                } else {
                  // Show institution details modal for non-institution users
                  this.handleInstitutionDetailsModal(subscriptionData, plan);
                }
              } else {
                this.reloadWithDelay();
              }
            });
        } else if (this.popoutWindow?.location.search.includes('state=cancelled')) {
          this.closeWindowAndClearInterval();
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Payment canceled'),
              description: this.translateService.instant('Checkout flow has been canceled.'),
            },
            true,
          );
        }
      } catch (error) {
        // While we continue search for succeed or cancelled we will getting error that we could not
      }
    };
    this.intervalId = modifiedSetInterval(checkPaymentStatus, 1000);
  }

  private handleInstitutionDetailsModal(
    subscriptionData: ChargebeeSubscriptionData,
    plan: Partial<PaymentPlan>,
  ): void {
    this.fullScreenModalService.open(InstitutionDetailsModalComponent, {
      data: {
        chargebeeSubscriptionData: subscriptionData,
        plan,
      },
    });
  }

  /**
   * Handles billing update for existing institution users
   */
  private handleInstitutionBillingUpdate(
    subscriptionData: ChargebeeSubscriptionData,
    plan: PaymentPlan,
  ): void {
    const currentUser = this.userService.user.getValue()!;

    const currentInstitution = currentUser?.user?.institution;

    const updatedInstitution: Institution = {
      ...currentInstitution,
      tierConfiguration: {
        tier: plan.tier ?? '',
      },
      paywallConfig: buildPaywallConfigFromPaymentPlan(plan),
      billingStatus: {
        subscription_status: SubscriptionStatus.ACTIVE,
        customerId: subscriptionData.customerId,
        subscription: {
          plan: subscriptionData.planId,
          chargebeeId: subscriptionData.subscriptionId,
          email: subscriptionData.chargebeeEmail,
        },
      },
    };

    // Call API to update institution billing details
    this.updateInstitutionBilling(updatedInstitution)
      .pipe(take(1))
      .subscribe((response) => {
        // Show success notification
        this.notificationToasterService.showDefaultSuccessNotification(
          this.translateService.instant('Your Plan Has Been Updated Successfully'),
          this.notificationToasterService.generateNotificationCode(),
        );

        // Redirect to landing page with postUpgrade query parameter after 5 seconds
        modifiedSetTimeout(() => {
          this.homeNavigation.hardRedirect({
            [ActionsQueryParamKeys.POST_INSTITUTION_UPGRADE]: 'true',
          });
        }, 5000);
      });
  }

  private reloadWithDelay(delayMs = 5000): void {
    modifiedSetTimeout(() => {
      window.location.reload();
    }, delayMs);
  }

  getBillingDetails(id: string): Observable<PostPayment> {
    return this.http.get<PostPayment>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/billingDetails/${id}`,
    );
  }

  createPortalSession(): Observable<PortalInfo> {
    return this.http.get<PortalInfo>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/createPortalSession`,
    );
  }

  createCheckout(planID: string): Observable<CheckoutInfo> {
    return this.http.get<CheckoutInfo>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/createCheckout/${planID}`,
      { headers: { [ErrorInterceptorSkipHeader]: '' } },
    );
  }

  switchPlanCheckout(planID: string): Observable<CheckoutInfo> {
    return this.http.get<CheckoutInfo>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/switchPlanCheckout/${planID}`,
      { headers: { [ErrorInterceptorSkipHeader]: '' } },
    );
  }

  checkCheckoutEligibility(): Observable<CheckoutEligibilityAllowedResponse> {
    return this.http.get<CheckoutEligibilityAllowedResponse>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/checkoutEligibility`,
      { headers: { [ErrorInterceptorSkipHeader]: '' } },
    );
  }

  checkoutSucceed(hosted_page_id: string): Observable<CheckoutSuccessResponse> {
    return this.http.get<CheckoutSuccessResponse>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/checkoutSucceed/${hosted_page_id}`,
      { params: { isInstitutionPlan: true } },
    );
  }

  updateInstitutionBilling(institution: Institution): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/updateInstitutionBilling`,
      { institution },
    );
  }

  showNotification(isSuccess: boolean, popupPageType: PopUpPageType, planId?: string): void {
    const notificationConfiguration: PayWallNotificationConfigurtion =
      this.getNotificationConfiguration(isSuccess, popupPageType);
    const topElement = new IconMessageToasterElement(
      { icon: notificationConfiguration.icon, size: 16 },
      this.translateService.instant(notificationConfiguration.notificationTopElement),
    );

    const notificationDataBuilder = new NotificationDataBuilder(
      isSuccess ? SUCCESSES.ACTION_SUCCESSFUL : ERRORS.ACTION_FAILED,
    )
      .type(notificationConfiguration.notificationType)
      .style(notificationConfiguration.toasterPopupStyle)
      .priority(380)
      .showProgressBar(true)
      .timeOut(5)
      .topElements([topElement]);

    const middleElement = new IconMessageToasterElement(
      undefined,
      this.translateService.instant(notificationConfiguration.notificationMiddleElement),
    );

    const notificationData = notificationDataBuilder.middleElements([middleElement]).build();
    this.notificationToasterService.showNotification(notificationData).then(() => {
      if (isSuccess) {
        this.telemetry.event(notificationConfiguration.telemetryEventData as string, {
          plan_id: planId,
        });
        this.reloadWithDelay();
      }
    });
  }

  private getNotificationConfiguration(
    isSuccess: boolean,
    checkoutType: PopUpPageType,
  ): PayWallNotificationConfigurtion {
    if (isSuccess) {
      return notificationConfigurationMap[checkoutType];
    } else {
      return notificationConfigurationMap['Failure'];
    }
  }

  updateHubspotContactProperties(propertyToUpdate: HubspotContactProperties): Observable<any> {
    return this.http.post<any>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/updateHubspotContactProperties`,
      {
        propertyToUpdate,
      },
    );
  }

  getSubscription(subscriptionId: string): Observable<SubscriptionInfo> {
    return this.http.get<SubscriptionInfo>(
      `${this.urlService.getDynamicUrl()}/tutor/billing/subscription/${subscriptionId}`,
    );
  }

  async handlePortalSession(planID: string): Promise<void> {
    await this.executeWithWindowRollback(async () => {
      const portalSession: PortalInfo = await firstValueFrom(this.createPortalSession());

      if (!portalSession) {
        throw new Error('Portal session not available');
      }

      if (!this.popoutWindow) {
        this.showPopUpBlockedError();
        return;
      }

      this.popoutWindow.location.href = portalSession.access_url;
      this.portalStateHandler(portalSession.subscriptionId, planID);
    });
  }

  /**
   * Opens the Chargebee portal for invoice review and resolves `true` once the popup closes.
   * Resolves `false` if the popup is blocked or the portal session fails (the user is already notified).
   */
  async handlePortalSessionForInvoiceReview(): Promise<boolean> {
    if (!this.popoutWindow) {
      this.showPopUpBlockedError();
      return false;
    }
    let portalSession: PortalInfo;
    try {
      portalSession = await firstValueFrom(this.createPortalSession());
    } catch (error) {
      this.closeWindowAndClearInterval();
      this.logMessages(error?.message);
      return false;
    }
    this.popoutWindow.location.href = portalSession.access_url;
    return new Promise<boolean>((resolve) => {
      this.intervalId = modifiedSetInterval(() => {
        if (this.popoutWindow?.closed) {
          this.closeWindowAndClearInterval();
          resolve(true);
        }
      }, 1000);
    });
  }

  private portalStateHandler(subscriptionId: string | undefined, planID: string) {
    const checkPortalState = () => {
      this.logMessages('portal session listener running');
      if (this.popoutWindow?.closed) {
        this.logMessages('portal session closed');
        this.closeWindowAndClearInterval();
        if (!subscriptionId) {
          return;
        }
        this.getSubscription(subscriptionId)
          .pipe(take(1))
          .subscribe((subscriptionInfo: SubscriptionInfo) => {
            if (
              subscriptionInfo &&
              subscriptionInfo.status === ChargebeeSubscriptionStatus.CANCELLED
            ) {
              this.showNotification(true, PopUpPageType.CancelSubscription, planID);
            }
          });
      }
    };
    this.intervalId = modifiedSetInterval(checkPortalState, 1000);
  }

  private showToastError(backendMessage?: string): void {
    this.logMessages('failed to open pop-up because an error occured');
    this.settingsService.showToast(
      {
        title: this.translateService.instant('Failed to open'),
        description: this.translateService.instant(
          backendMessage || 'An error occurred. Please try again.',
        ),
      },
      true,
    );
  }

  private showPopUpBlockedError(): void {
    this.logMessages('failed to open pop-up due to blockage');
    this.settingsService.showToast(
      {
        title: this.translateService.instant('Pop-up blocked by browser'),
        description: this.translateService.instant(
          'Your browser settings are preventing us from loading our payment portal. Please re-enable browser pop-ups and try again.',
        ),
      },
      true,
    );
  }

  private closeWindowAndClearInterval(): void {
    if (this.popoutWindow && !this.popoutWindow.closed) {
      this.popoutWindow?.close();
      this.popoutWindow = null;
    }
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  hasBillingStrategyWithHiddenBillingUi(institution?: Institution): boolean {
    return institution?.paywallConfig?.billingStrategy === BillingStrategy.CUSTOM_BASED;
  }

  fetchBillingPlanDetails(): Observable<CustomerPlan> {
    return this.http.get<CustomerPlan>(`${this.urlService.getDynamicUrl()}/tutor/billing/plan`);
  }

  getPlanDetailsByBillingType(billingType: BillingType): Observable<CustomerPlan> {
    if (billingType === BillingType.Automatic) {
      return this.fetchBillingPlanDetails();
    }

    return of({
      planName: this.translateService.instant('Enterprise - Custom'),
      billingFrequency: BillingFrequency.Monthly,
    });
  }

  private logInstitutionUpgradeToFullStory(
    plan: PaymentPlan,
    subscriptionData: ChargebeeSubscriptionData,
  ): void {
    const eventProperties = {
      planDetails: {
        name: plan.name,
        billingStrategy: plan.billingStrategy,
        tier: plan.tier,
        planId: plan.planId,
        billingFrequency: plan.billingFrequency,
        price: plan.price,
      },
      billingDetails: {
        customerId: subscriptionData.customerId,
        subscriptionId: subscriptionData.subscriptionId,
        chargebeeEmail: subscriptionData.chargebeeEmail,
        planId: subscriptionData.planId,
      },
    };

    // Log to FullStory
    this.telemetry.logBasicEvent('[Institution Checkout Upgrade Completed]', eventProperties);
  }

  /**
   * Track conversion with actual subscription data from Chargebee
   * Uses GTM dataLayer (GTM-WNMQRX4) for unified tracking across all properties
   * Complete funnel visibility from ad click to purchase
   */
  private trackConversionWithSubscriptionData(
    plan: PaymentPlan,
    subscriptionData: ChargebeeSubscriptionData,
    checkoutType: PopUpPageType,
  ): void {
    try {
      // Push purchase event to GTM dataLayer for GA4 and Google Ads tracking
      this.gtmService.pushTag({
        event: 'purchase',
        // Standard ecommerce object - used by Google Ads & GA4
        ecommerce: {
          transaction_id: subscriptionData.subscriptionId, // Critical: prevents duplicate conversions
          value: plan.price, // Total transaction value
          currency: subscriptionData.currency, // ISO 4217 currency code from Chargebee
          items: [
            {
              item_id: plan.planId,
              item_name: plan.name,
              price: plan.price,
              quantity: 1,
            },
          ],
        },
        // Custom metadata for enhanced tracking and internal analytics
        customer_id: subscriptionData.customerId,
        chargebee_email: subscriptionData.chargebeeEmail,
        plan_name: plan.name,
        billing_frequency: plan.billingFrequency,
        billing_strategy: plan.billingStrategy,
        checkout_type: checkoutType,
      });

      // Log to FullStory for tracking and debugging
      this.telemetry.logBasicEvent('[GTM Purchase Event Tracked]', {
        gtm_container_id: environment.gtmId,
        transaction_id: subscriptionData.subscriptionId,
        plan_name: plan.name,
      });
    } catch (error) {
      console.error('GTM dataLayer tracking error:', error);
    }
  }

  /**
   * Starts the free trial checkout flow.
   *
   * This method opens the Chargebee checkout portal/popup and automatically selects the trial plan
   * (e.g., `Trial-plan-USD-Every-2-weeks`).
   *
   * @returns {Promise<void>}
   */
  async startFreeTrial(): Promise<void> {
    this.openBufferWindow(`${PopUpPageType.CreateSubscribtion} session`);
    await this.executeWithWindowRollback(async () => {
      const trialPlanId = this.flagsService.featureFlagsVariables[FLAGS.ENABLE_BILLING]
        ?.free_trial_plan_id as string;
      const plan = await firstValueFrom(this.planDetailsService.getPlanByPlanId(trialPlanId));
      if (!plan) {
        throw new Error(`Trial plan not found: ${trialPlanId}`);
      }

      await this.handleCheckout(PopUpPageType.CreateSubscribtion, plan, false);
    });
  }

  /**
   * Checks if an institution is on a trial plan.
   *
   * This method fetches the institution's plan details and checks if it's a trial plan.
   *
   * @param {Institution} institution - The institution to check.
   * @returns {Promise<boolean>} - True if the institution is on a trial plan, false otherwise.
   */
  async checkIsTrialInstitution(institution: Institution): Promise<boolean> {
    const subscription = <Subscription>institution.billingStatus?.subscription;
    const planId = subscription?.plan;
    const plan = await firstValueFrom(this.planDetailsService.getPlanByPlanId(planId));
    return Boolean(plan?.isTrialPlan);
  }
}
